<template>
  <b-modal
    v-model="showModal"
    v-if="projectMetric"
    hide-footer
    size="lg"
    @ok.prevent="cancel"
  >
    <template #modal-header="{ close }">
      <div>
        <h2 class="modal-title mb-2">{{ projectMetric.name }}</h2>
        <div v-if="projectMetric.type">
          <div
            v-for="(type, i) in projectMetric.type.split(',')"
            :key="i"
            :class="'metric-modal-row lvl ' + type.toLowerCase()"
          >
            <span
                class="mr-2 tag-item"
              >{{ type }}
              </span>
          </div>
        </div>
      </div>
      <div>
        <button type="button" aria-label="Close" class="close mb-2" @click="close()">×</button>
        <div v-if="isMeeting" class="modal-row">
          <b-button
              v-on:click="editProjectMetric(projectMetric.projectMetricId)"
              variant="primary"
            >
              New Status Update
            </b-button>
        </div>
      </div>
      
    </template>
    <div v-if="projectMetric.dateDue" class="modal-row due">
      <p>
        <span>{{ getTitle("dateDue") }}:</span>
        {{ toDisplayDate(projectMetric.dateDue) }}
      </p>
    </div>
    <div v-if="projectMetric.description" class="modal-row description">
      <h3>{{ getTitle("description") }}</h3>
      <p class="pre-wrap">{{ projectMetric.description }}</p>
    </div>
    <div v-if="projectMetric.tags" class="modal-row tags">
      <p>
        <span>{{ getTitle("tags") }}:</span>
        <span
          class="mx-1 tag-item"
          v-for="(tag, i) in projectMetric.tags.split(',')"
          :key="i"
        >{{ tag }}</span>
      </p>
    </div>
    <div v-if="projectMetric.dateIdentified" class="modal-row identified">
      <p>
        <span>{{ getTitle("dateIdentified", "Date Identified") }}:</span>
        {{ toDisplayDate(projectMetric.dateIdentified) }}
      </p>
    </div>
    <div v-if="projectMetric.assignedToUsers.length > 0" class="modal-row assigned-to">
      <p>
        <span>{{ getTitle("assignedToUsers", "Champions") }}:</span>
        {{ projectMetric.assignedToUsers.map((u) => u.name).join(", ") }}
      </p>
    </div>
    <div v-if="projectMetric.dateCreated" class="modal-row created-date">
      <p>
        <span>{{ getTitle("dateCreated", "Date Created") }}:</span>
        {{ toDisplayDate(projectMetric.dateCreated) }}
      </p>
    </div>
    <div v-if="projectMetric.createdBy" class="modal-row created-by">
      <p>
        <span>{{ getTitle("createdBy") }}:</span>
        {{ projectMetric.createdBy.name }}
      </p>
    </div>
    <div v-if="projectMetric.identifiedByRole" class="modal-row identified-role">
      <p>
        <span>{{ getTitle("identifiedByRole") }}:</span>
        {{ projectMetric.identifiedByRole }}
      </p>
    </div>
    <div v-if="projectMetric.resolutionLevel" class="modal-row resolution">
      <p>
        <span>{{ getTitle("resolution") }}:</span>
        {{ projectMetric.resolution }}
      </p>
    </div>
    <div v-if="projectMetric.resolutionLevel" class="modal-row resolution-lvl">
      <p>
        <span>{{ getTitle("resolutionLevel") }}:</span>
        {{ projectMetric.resolutionLevel }}
      </p>
    </div>
    <div v-if="projectMetric.dateResolved" class="modal-row resolution-date">
      <p>
        <span>{{ getTitle("dateResolved") }}:</span>
        {{ toDisplayDate(projectMetric.dateResolved) }}
      </p>
    </div>
    <div v-if="projectMetric.resolvedBy" class="modal-row resolved-by">
      <p>
        <span>{{ getTitle("resolvedBy") }}:</span>
        {{ projectMetric.resolvedBy }}
      </p>
    </div>
    <div v-if="projectMetric.timeSaved" class="modal-row time-saved">
      <p>
        <span>{{ timeSavedTitle() }}:</span>
        {{ projectMetric.timeSaved }}
      </p>
    </div>
    <div v-if="projectMetric.costSaved" class="modal-row cost-saved">
      <p>
        <span>{{ costSavedTitle() }}:</span>
        {{ toDisplayDollars(projectMetric.costSaved) }}
      </p>
    </div>
    <div v-if="projectMetric.mitigationMethod" class="modal-row method">
      <p>
        <span>{{ getTitle("mitigationMethod") }}:</span>
        {{ projectMetric.mitigationMethod }}
      </p>
    </div>
    <div v-if="projectMetric.mitigationMethodData" class="modal-row method-data">
      <strong
        v-if="
          projectMetric.mitigationMethod === 'VECP' ||
          projectMetric.mitigationMethod === 'Change Order (non VECP)'
        "
      >Change Order #:</strong>
      <strong v-if="projectMetric.mitigationMethod === 'Potential Claim'">Potential Claim #:</strong>
      {{ projectMetric.mitigationMethodData }}
      <br />
    </div>
    <div v-if="projectMetric.participants" class="modal-row">
      <p>
        <span>{{ getTitle("participants") }}:</span>
        {{ projectMetric.participants.split(",").join(", ") }}
      </p>
    </div>
    <div v-if="projectMetric.dateUpdated" class="modal-row update-date">
      <p>
        <span>{{ getTitle("dateUpdated") }}:</span>
        {{ toDisplayDate(projectMetric.dateUpdated) }}
      </p>
    </div>
    <div v-if="projectMetric.updatedBy" class="modal-row updated-by">
      <p>
        <span>{{ getTitle("updatedBy") }}:</span>
        {{ projectMetric.updatedBy.name }}
      </p>
    </div>
    
    <h3>Comments</h3>
    <div v-if="comments.length">
        <div v-for="comment in comments" v-bind:key="comment.noteId">
        <b-overlay :show="comment.sending" variant="light" opacity="0.85" blur="2x" rounded="sm">
          <b-form class="metric-comment" @submit.prevent="onEditCommentSubmit">
            <b-row>
              <b-col>
                <div
                  style="
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                  "
                >
                  <div>
                    <img v-if="comment.logoUrl" :src="comment.logoUrl" class="table-logo" />
                    <span>{{ comment.createdBy.name }}</span>
                  </div>
                  <span class="float-right comment-date">{{ dateFormat(comment.date) }}</span>
                </div>
              </b-col>
            </b-row>
            <b-row></b-row>
            <div class="metric-comment-content">
              <b-row style="flex-grow: 5">
                <b-col>
                  <b-form-group v-if="comment.editing" for="edit-comment">
                    <b-form-input id="edit-comment" v-model.trim="comment.newContent"></b-form-input>
                  </b-form-group>
                  <div v-else>{{ comment.content }}</div>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <b-button
                    variant="tertiary"
                    v-if="comment.editing"
                    @click="cancelEditing(comment.commentId)"
                  >
                    <b-icon icon="x" aria-label="Cancel" title="Cancel">Cancel</b-icon>
                  </b-button>
                  <b-button
                    variant="tertiary"
                    v-else-if="comment.createdBy.id === accountUser.userId"
                    @click="showDeleteConfirmation(comment.commentId, false)"
                    :disabled="deletingCommentId !== ''"
                  >
                    <b-icon icon="trash-fill" aria-label="Delete" title="Delete">Delete</b-icon>
                  </b-button>
                  <b-button
                    variant="tertiary"
                    v-if="
                      comment.createdBy.id === accountUser.userId &&
                      !comment.editing
                    "
                    :disabled="editingCommentId !== ''"
                    @click="startEditing(comment.commentId)"
                  >
                    <b-icon icon="pencil-square" aria-label="Edit" title="Edit">Edit</b-icon>
                  </b-button>
                  <b-button
                    variant="tertiary"
                    v-else-if="comment.editing"
                    @click="onEditCommentSubmit()"
                  >
                    <b-icon icon="check2" aria-label="Save" title="Save">Save</b-icon>
                  </b-button>
                </b-col>
              </b-row>
            </div>
          </b-form>
        </b-overlay>
      </div>
    </div>
    <div v-else class="text-center">No comments yet.</div>
    <BaseConfirmationModal
      ref="delete-comment-confirmation"
      message="Are you sure you want to delete this comment?"
      @submit="onDeleteCommentConfirm"
      @cancel="cancelDelete"
    ></BaseConfirmationModal>
  </b-modal>
</template>

<script>
import axios from "axios";
import pmMixin from "@/mixins/project-metric-mixin.js";
import BaseConfirmationModal from "@/components/BaseConfirmationModal.vue";

export default {
  name: "ViewProjectMetricModal",
  mixins: [pmMixin],
  components: {
    BaseConfirmationModal,
  },
  props: {
    projectMetric: { type: Object, default: null },
  },
  data() {
    return {
      showModal: false,
      form: this.newForm(),
      sending: false,
      deletingCommentId: "",
      editingCommentId: "",
      comments: [],
      isMeeting: false
    };
  },
  methods: {
    show() {
      console.log('show');
      return (this.showModal = true);
    },
    hide() {
      return (this.showModal = false);
    },
    cancel() {
      this.reset();
      this.hide();
    },
    reset() {
      this.form = this.newForm();
      this.deletingCommentId = "";
      this.editingCommentId = "";
      this.sending = false;
    },
    editProjectMetric(id) {
      this.$parent.openEditModal(id, true);
      this.cancel();
    },

    startEditing(id) {
      this.editingCommentId = id;
      this.comments.find((c) => c.commentId === id).editing = true;
      this.comments.find(
        (c) => c.commentId === id
      ).newContent = this.comments.find((c) => c.commentId === id).content;
    },
    cancelEditing(id) {
      this.editingCommentId = "";
      this.comments.find((c) => c.commentId === id).editing = false;
      this.comments.find(
        (c) => c.commentId === id
      ).newContent = this.comments.find((c) => c.commentId === id).content;
    },
    showDeleteConfirmation(commentId) {
      this.deletingCommentId = commentId;
      this.$refs["delete-comment-confirmation"].show();
    },
    cancelDelete() {
      this.deletingCommentId = "";
    },
    onEditCommentSubmit() {
      let comment = this.comments.find((c) => c.commentId === this.editingCommentId);

      if (comment.newContent === "") {
        return;
      }

      let request = {
        commentId: comment.commentId,
        lastEdited: new Date(),
        content: comment.newContent,
      };
      comment.sending = true;
      axios
        .post(
          `${this.$api_url}/project-metrics/comments/update`,
          request,
          this.getReqConfig()
        )
        .then(() => {
          this.form = this.newForm();
          this.$emit("refresh", this.projectMetric.projectMetricId);
        })
        .catch(() => {
          })
        .finally(() => {
          comment.sending = false;
          this.editingCommentId = "";
        });
    },
    onDeleteCommentConfirm() {
      this.comments.find(
        (c) => c.commentId === this.deletingCommentId
      ).sending = true;
      axios
        .delete(
          `${this.$api_url}/project-metrics/comments/${this.deletingCommentId}/delete`,
          this.getReqConfig()
        )
        .then(() => {
          this.$emit("refresh", this.projectMetric.projectMetricId);
        })
        .catch(() => {
          this.comments.find(
            (c) => c.commentId === this.deletingCommentId
          ).sending = false;
        })
        .finally(() => {
          this.deletingCommentId = "";
        });
    },
    newForm() {
      return {
        comment: "",
      };
    },
    dateFormat(date) {
      date = new Date(date);
      let line =
        date.toLocaleString("default", {
          month: "long",
          day: "numeric",
          year: "numeric",
        });
      return line;
    },
    toDisplayDate(date) {
      return this.$helpers.displayDate(date);
    },
    toDisplayDollars(value) {
      return this.$helpers.formatDollars(value);
    },
    timeSavedTitle() {
      return this.projectMetric.type === 'Risk' || this.projectMetric.type === 'Issue' ?
        'Days Lost' :
        'Days Saved';
    },
    costSavedTitle() {
      return this.projectMetric.type === 'Risk' || this.projectMetric.type === 'Issue' ?
        'Dollars Lost' :
        'Dollars Saved';
    },
    updateIsMeeting() {
      this.isMeeting = window.location.href.includes('meeting');
    }
  },
  watch: {
    projectMetric(newVal) {
      if(!newVal.comments.length == 0){
        this.comments = newVal.comments.map((c) => ({
          ...c,
        editing: false,
        deleting: false,
        sending: false,
        newContent: c.content,
      }));

        this.comments.sort((a, b) => (a.date > b.date ? -1 : 1));
      } else{
        this.comments = [];
      }
    },
  },
  mounted() {
    this.updateIsMeeting();
  }
};
</script>